import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Backdrop,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { ResStatus } from 'api/api.types';
import {
  useGetMailAddresses,
  useGetNotificationStub,
  useGetWaybill,
  useSendNotification,
  useSendWaybill,
} from 'api/documentMails';
import { StyledTextField } from 'components/Inputs';
import { useAuth } from 'contexts/authContext';
import { appToast } from 'utils/toast.utils';
import * as S from '../../styles';
import { BUTTONS, DIALOGS } from '../../TransportCloudTable.consts';
import { EmailChipInput } from './EmailChipInput';
import { TextEditor, type TextEditorHandle } from './TextEditor';

export type MailType = 'notification' | 'waybill';

const MAIL_CONFIG: Record<MailType, { title: string; successMessage: string }> =
  {
    notification: {
      title: DIALOGS.notificationLabel,
      successMessage: DIALOGS.notificationSuccessMessage,
    },
    waybill: {
      title: DIALOGS.waybillLabel,
      successMessage: DIALOGS.waybillSuccessMessage,
    },
  };

const SUBJECT_SX = { '& .MuiInputBase-root': { fontSize: '14px' } } as const;
const SUBJECT_INPUT_STYLE = { padding: '4px 8px' } as const;

type Props = {
  open: boolean;
  type: MailType;
  jobId: number;
  jobEntryIds: number[];
  counterpartyIds: number[];
  onClose: () => void;
  onAfterSend?: () => void;
};

export const MailSenderDialog: React.FC<Props> = ({
  open,
  type,
  jobId,
  jobEntryIds,
  counterpartyIds,
  onClose,
  onAfterSend,
}) => {
  const { user } = useAuth();

  const [toEmails, setToEmails] = useState<string[]>([]);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [bccEmails, setBccEmails] = useState<string[]>([]);
  const [isToInputValid, setIsToInputValid] = useState(true);
  const [isDwInputValid, setIsDwInputValid] = useState(true);
  const [isUdwInputValid, setIsUdwInputValid] = useState(true);
  const [subject, setSubject] = useState('');
  const [editorContent, setEditorContent] = useState('');

  const { loading: notificationStubLoading, getNotificationStub } =
    useGetNotificationStub();
  const { loading: waybillStubLoading, getWaybill } = useGetWaybill();
  const {
    data: mailAddressData,
    loading: mailAddressesLoading,
    getMailAddresses,
  } = useGetMailAddresses();
  const { loading: sendingNotification, sendNotification } =
    useSendNotification();
  const { loading: sendingWaybill, sendWaybill } = useSendWaybill();

  const editorRef = useRef<TextEditorHandle>(null);
  const loadSessionRef = useRef<object | null>(null);

  const stubLoading =
    type === 'notification' ? notificationStubLoading : waybillStubLoading;
  const isSending =
    type === 'notification' ? sendingNotification : sendingWaybill;
  const isLoading = stubLoading || mailAddressesLoading;

  const canSend =
    !isSending &&
    !isLoading &&
    toEmails.length + ccEmails.length + bccEmails.length > 0 &&
    subject.trim().length > 0 &&
    isToInputValid &&
    isDwInputValid &&
    isUdwInputValid;

  useEffect(() => {
    if (!open) {
      loadSessionRef.current = null;
      return;
    }
    if (loadSessionRef.current) return;
    const loadSession = {};
    loadSessionRef.current = loadSession;

    if (jobEntryIds.length > 0) {
      const fetchStub =
        type === 'notification'
          ? getNotificationStub({ jobEntryIds })
          : getWaybill({ jobEntryIds });

      fetchStub.then((res) => {
        if (loadSessionRef.current !== loadSession) return;
        if (res?.data?.status !== ResStatus.OK) {
          onClose();
          return;
        }
        const stub = res.data.result;
        setSubject(stub.subject ?? '');
        setToEmails(stub.to ?? []);
        setCcEmails(stub.cc ?? []);
        setBccEmails(stub.bcc ?? []);
        setEditorContent(stub.text ?? '');
      });
    }
    if (counterpartyIds.length > 0) {
      getMailAddresses({ counterpartyIds });
    }
  }, [
    open,
    type,
    jobEntryIds,
    counterpartyIds,
    getNotificationStub,
    getWaybill,
    getMailAddresses,
    onClose,
  ]);

  useEffect(
    () => () => {
      loadSessionRef.current = null;
    },
    [],
  );

  const emailOptions = useMemo(
    () => Array.from(new Set(mailAddressData ?? [])),
    [mailAddressData],
  );

  const handleSend = async () => {
    const from = user?.emailCredentials?.login ?? '';
    const entities = jobEntryIds.map((id) => ({
      id,
      __type: 'ContainerJobEntry' as const,
    }));
    const mailMessage = {
      from,
      replyTo: from,
      to: toEmails,
      cc: ccEmails,
      bcc: bccEmails,
      subject,
      text: editorRef.current?.getHTML() ?? '',
    };
    const params = {
      jobId,
      payload: { entities, languageCode: 'pl', mailMessage },
    };

    const res = await (type === 'notification'
      ? sendNotification(params)
      : sendWaybill(params));

    if (res?.data?.status === ResStatus.OK) {
      appToast.success(MAIL_CONFIG[type].successMessage);
      onClose();
      if (type === 'waybill') {
        onAfterSend?.();
      }
    }
  };

  const { title } = MAIL_CONFIG[type];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: isLoading
          ? { width: 800, height: 539.175, margin: '1rem 2rem' }
          : { width: 800, maxHeight: '95vh', margin: '1rem 2rem' },
      }}
    >
      <DialogTitle sx={{ padding: '0.65rem 1rem', fontWeight: 600 }}>
        {DIALOGS.mailSenderHeader(title)}
      </DialogTitle>

      <DialogContent dividers sx={{ padding: '0.75rem 1rem' }}>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200,
            }}
          >
            <CircularProgress size={48} />
          </Box>
        ) : (
          <>
            <EmailChipInput
              label={DIALOGS.toLabel}
              value={toEmails}
              onChange={setToEmails}
              options={emailOptions}
              onPendingValidityChange={setIsToInputValid}
            />
            <EmailChipInput
              label={DIALOGS.ccLabel}
              value={ccEmails}
              onChange={setCcEmails}
              options={emailOptions}
              onPendingValidityChange={setIsDwInputValid}
            />
            <EmailChipInput
              label={DIALOGS.bccLabel}
              value={bccEmails}
              onChange={setBccEmails}
              options={emailOptions}
              onPendingValidityChange={setIsUdwInputValid}
            />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '52px 1fr',
                columnGap: '12px',
                mb: 1.5,
              }}
            >
              <Typography
                variant='body2'
                sx={{ whiteSpace: 'nowrap', mt: '6px' }}
              >
                {DIALOGS.subjectLabel}
              </Typography>
              <StyledTextField
                fullWidth
                size='small'
                variant='outlined'
                padding='dense'
                multiline
                minRows={1}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                style={{ width: '100%' }}
                inputProps={{ style: SUBJECT_INPUT_STYLE }}
                InputProps={{ style: { padding: 0, fontSize: '14px' } }}
                sx={SUBJECT_SX}
              />
            </Box>
            <TextEditor ref={editorRef} initialContent={editorContent} />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: '0.5rem 1rem' }}>
        <S.TCStyledButton $primary onClick={handleSend} disabled={!canSend}>
          {BUTTONS.send}
        </S.TCStyledButton>
        <S.TCStyledButton onClick={onClose}>{BUTTONS.cancel}</S.TCStyledButton>
      </DialogActions>

      <Backdrop
        open={isSending}
        sx={{
          backgroundColor: 'rgba(0,0,0,0.3)',
          zIndex: (theme) => theme.zIndex.modal + 1,
        }}
      >
        <CircularProgress />
      </Backdrop>
    </Dialog>
  );
};
